import React, { FunctionComponent, useEffect } from 'react';
import { graphql } from 'react-relay';
import { usePaginationFragment } from 'react-relay/hooks';
import { CatalogListContent } from 'src/components/widgets/catalogList';
import { CatalogListItemModel } from 'src/components/widgets/catalogList/catalogListItemModel';
import { T } from 'src/shared/helpers';
import { RelayFragment } from 'src/shared/types';
import { useProductConfigurationState } from '../../state';
import { productProvider_listItem$data } from '../../state/queries/__generated__/productProvider_listItem.graphql';
import { importCatalogListQuery$data } from './__generated__/importCatalogListQuery.graphql';
import { importCatalogPaginationFragmentQuery } from './__generated__/importCatalogPaginationFragmentQuery.graphql';
import { importCatalogPaginationFragment__providerProducts$key } from './__generated__/importCatalogPaginationFragment__providerProducts.graphql';

// List component
interface ImportCatalogPaginationFragmentProps {
    query: importCatalogListQuery$data;
    search: string;
    category: string | null;
    macroCategory: string | null;
    onProductClick: (providerProduct: Omit<RelayFragment<productProvider_listItem$data>, ' $fragmentType'>) => void;
}

const ImportCatalogPaginationFragment: FunctionComponent<ImportCatalogPaginationFragmentProps> = (props) => {
    const { state } = useProductConfigurationState();

    const { data, loadNext, hasNext, refetch, isLoadingNext } = usePaginationFragment<
        importCatalogPaginationFragmentQuery,
        importCatalogPaginationFragment__providerProducts$key
    >(
        graphql`
            fragment importCatalogPaginationFragment__providerProducts on Query
            @refetchable(queryName: "importCatalogPaginationFragmentQuery")
            @argumentDefinitions(
                count: { type: "Int", defaultValue: 1 }
                cursor: { type: "String", defaultValue: "1" }
                search: { type: "String", defaultValue: "" }
                category: { type: "String", defaultValue: null }
                macroCategory: { type: "String", defaultValue: null }
                provider: { type: "ProductProvider", defaultValue: PRINTFUL }
            ) {
                providerProducts(first: $count, after: $cursor, search: $search, category: $category, macroCategory: $macroCategory, provider: $provider)
                    @connection(key: "importCatalogPaginationFragment_providerProducts") {
                    edges {
                        node {
                            ...productProvider_listItem @relay(mask: false)
                        }
                    }
                }
            }
        `,
        props.query
    );

    useEffect(() => {
        refetch(
            {
                count: 1,
                provider: state.provider,
                search: props.search,
                category: props.category,
                macroCategory: props.macroCategory
            },
            { fetchPolicy: 'store-and-network' }
        );
    }, [props.search]);

    const handleProductClick = (model: CatalogListItemModel) => {
        const providerProduct = data.providerProducts?.edges?.find((x) => x?.node?.id == model.id);
        if (providerProduct && providerProduct.node) {
            props.onProductClick(providerProduct.node);
        }
    };

    return (
        <CatalogListContent
            models={
                data.providerProducts?.edges?.map((x) => ({
                    id: x?.node?.id!,
                    subtitle: '',
                    title: x?.node?.name ?? '',
                    thumbnail: x?.node?.imageUrl,
                    selected: false
                })) ?? []
            }
            columns={{ xs: 1, md: 5, lg: 8 }}
            loadNext={() => loadNext(1)}
            hasNext={hasNext}
            isLoadingNext={isLoadingNext}
            onItemClick={handleProductClick}
            noItemsMessage={T._('No products found', 'Admin')}
        />
    );
};

export default ImportCatalogPaginationFragment;
